import { AriaButtonProps, useButton } from '@react-aria/button'
import { HoverProps, useHover } from '@react-aria/interactions'
import { useFocusRing } from '@react-aria/focus'
import { filterDOMProps, mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import Styles from './FormButton.module.scss'
import { forwardRef, PropsWithChildren } from 'react'
import { useObjectRef } from 'utils/useObjectRef'
import { AriaBaseButtonProps } from '@react-types/button'
import { Icon } from 'blaze-ui/icons/Types'
import { ChevronDown, ChevronUp } from 'blaze-ui/icons'
import { ButtonProps } from 'blaze-ui/Button/Types'

export interface FormButtonButtonProps extends AriaButtonProps, AriaBaseButtonProps, HoverProps, ButtonProps {
  icon?: Icon
}

export const FormButton = forwardRef<HTMLButtonElement, PropsWithChildren<FormButtonButtonProps>>((props, fRef) => {
  const ref = useObjectRef<HTMLButtonElement>(fRef)
  const { children, icon: Icon, variant, ...finalProps } = props
  const { buttonProps, isPressed } = useButton(props, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <button
      {...mergeProps(filterDOMProps(finalProps), buttonProps, hoverProps, focusProps)}
      className={cx(Styles.root, {
        [Styles.disabled]: buttonProps.disabled,
        [Styles.hovered]: isHovered,
        [Styles.pressed]: isPressed,
        [Styles.focused]: isFocusVisible,
        [Styles.tertiary]: variant === 'tertiary',
      })}
      ref={ref}
    >
      {Icon && (
        <div className={Styles.icon}>
          <Icon />
        </div>
      )}
      {children && <span className={Styles.children}>{children}</span>}
      {isPressed ? <ChevronUp size={16} color="var(--dark-40)" /> : <ChevronDown size={16} color="var(--dark-40)" />}
    </button>
  )
})
